import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";

import { QueryCacheAuthBoundary } from "@bw/shared/components";
import { financeApi } from "@bw/shared/api";
import { FinanceConnectIdentityBoundary } from "@/components/finance-connect/FinanceConnectIdentityBoundary";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { LaunchLayout } from "./layouts/LaunchLayout";
import { AuthLayout } from "./layouts/AuthLayout";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireApprovedOrg } from "./auth/RequireApprovedOrg";
import { NavRouteGate } from "./navigation/NavRouteGate";
import {
  COURT_SETUP_ALLOWED_STATUSES,
  STAFF_SETUP_ALLOWED_STATUSES,
} from "./org-access-policy";
import { FacilityGate } from "./layouts/facility-gate";
import { OrganizationProvider } from "./OrganizationContext";
import { WorkspaceChooser } from "@/components/organization/WorkspaceChooser";
import { StaffInvitationPage } from "@/components/staff/StaffInvitationPage";
import { OrgDashboardPage } from "@/components/org/org-dashboard";
import { CreateFacility } from "@/components/facility/create-facility";
import { OnboardingRouter } from "./onboarding/OnboardingRouter";
import { PendingConfirmationPage } from "@/components/onboarding/PendingConfirmationPage";
import { StripeConnectSetupPage } from "@/components/onboarding/pages/StripeConnectSetupPage";
import { OperatorDashboard } from "@/components/dashboard/operator-dashboard";
import { GettingStarted } from "@/components/onboarding/getting-started";
import { NotificationsPage } from "@/components/notifications/notifications-page";
import { CoachWorkspace } from "@/components/coaching/coach-workspace";
import { CoachingRequestDetail } from "@/components/coaching/coaching-request-detail";
import { InquiryWorkspace } from "@/components/inquiries/inquiry-workspace";
import { InquiryThread } from "@/components/inquiries/inquiry-thread";
import { ArenaWorkspace } from "@/components/arena/arena-workspace";
import { CheckInWorkspace } from "@/components/check-in/check-in-workspace";
import { ArrivalsConsole } from "@/components/front-desk/arrivals-console";
import { KioskDevicesPage } from "@/components/front-desk/kiosk/kiosk-devices-page";
import { CourtsWorkspace } from "@/components/courts/courts-workspace";
import { CalendarWorkspace } from "@/components/calendar/calendar-workspace";
import { BookingsWorkspace } from "@/components/bookings/bookings-workspace";
import { TournamentsWorkspace } from "@/components/tournaments/tournaments-workspace";
import { TournamentDetail } from "@/components/tournaments/tournament-detail";
import { NewTournamentPage } from "@/components/tournaments/new-tournament-page";
import { TournamentControlCenterPage } from "@/components/tournaments/control-center";
import { TournamentScheduleReviewPage } from "@/components/tournaments/tournament-schedule-review-page";
import { TournamentTabRedirect } from "@/components/tournaments/tournament-tab-redirect";
import { ProgramsList } from "@/components/programs/programs-list";
import { ProgramCreate } from "@/components/programs/program-create";
import { ProgramDetail } from "@/components/programs/program-detail";
import { LeaguesList } from "@/components/leagues/leagues-list";
import { LeagueDetail } from "@/components/leagues/league-detail";
import { SeasonDetail } from "@/components/leagues/season-detail";
import { CommunityPage } from "@/components/community/community-page";
import { FacilitySettings } from "@/components/facility/facility-settings";
import { ClaimPlacePage } from "@/components/facility/claim-place-page";
import { OrgSettings } from "@/components/org/org-settings";
import { ContractPage } from "@/components/contract/contract-page";
import { ScopedCapabilitiesPage } from "@/components/org/ScopedCapabilitiesPage";
import { ModuleOffersPage } from "@/components/commercial/ModuleOffersPage";
import { ModuleOfferReviewPage } from "@/components/commercial/ModuleOfferReviewPage";
import { ModuleCommitmentsPage } from "@/components/commercial/ModuleCommitmentsPage";
import { ModuleAgreementPage } from "@/components/commercial/ModuleAgreementPage";
import { CommercialReadinessPage } from "@/components/commercial/CommercialReadinessPage";
import { MembershipsPage } from "@/components/memberships/memberships-page";
import { PromotionsWorkspace } from "@/components/promotions/promotions-workspace";
import { RatesWorkspace } from "@/components/rates/rates-workspace";
import { DropInWorkspace } from "@/components/drop-in/drop-in-workspace";
import { DropInServiceHome } from "@/components/drop-in/drop-in-service-home";
import { CourtReservationServiceHome } from "@/components/court-reservation/court-reservation-service-home";
import { StaffPage } from "@/components/staff/staff-page";
import { RevenueAnalyticsWorkspace } from "@/components/analytics/revenue-analytics-workspace";
import { BookingAnalyticsWorkspace } from "@/components/analytics/booking-analytics-workspace";
import { OperationalInsightsWorkspace } from "@/components/analytics/operational-insights-workspace";
import { ProgramInsightsWorkspace } from "@/components/analytics/program-insights-workspace";
import { GrowthInsightsWorkspace } from "@/components/analytics/growth-insights-workspace";
import { InstructorInsightsWorkspace } from "@/components/analytics/instructor-insights-workspace";
import { ServiceRetentionInsightsWorkspace } from "@/components/analytics/service-retention-insights-workspace";
import { SettlementsWorkspace } from "@/components/analytics/settlements-workspace";
import { PaymentsWorkspace } from "@/components/payments";
import { PrivacyPage } from "@/components/legal/privacy";
import { TermsPage } from "@/components/legal/terms";
import { SignInPage } from "./auth/pages/SignInPage";
import { SignUpPage } from "./auth/pages/SignUpPage";
import { ForgotPasswordPage } from "./auth/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./auth/pages/ResetPasswordPage";
import { LegacyLoginRedirect } from "./auth/LegacyLoginRedirect";
import {
  ForbiddenPage,
  UnauthorizedPage,
  NotFoundPage,
  InternalServerErrorPage,
  UnderMaintenancePage,
} from "./auth/pages/errors";

const router = createBrowserRouter([
  {
    // (auth) route group — full-screen chrome, no app shell, NOT guarded.
    // RequireAuth must not wrap these or an unauthenticated user could never
    // reach the sign-in form (infinite redirect).
    element: <AuthLayout />,
    children: [
      { path: "sign-in", element: <SignInPage /> },
      { path: "sign-up", element: <SignUpPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },

      // Aliases — keep the business-v2 URLs alive (login/register).
      { path: "login", element: <LegacyLoginRedirect /> },
      { path: "register", element: <Navigate to="/sign-up" replace /> },

      // Error screens.
      { path: "errors/forbidden", element: <ForbiddenPage /> },
      { path: "errors/unauthorized", element: <UnauthorizedPage /> },
      { path: "errors/not-found", element: <NotFoundPage /> },
      {
        path: "errors/internal-server-error",
        element: <InternalServerErrorPage />,
      },
      { path: "errors/under-maintenance", element: <UnderMaintenancePage /> },
    ],
  },
  {
    // Dashboard route group — rendered under the app shell, guarded by
    // RequireAuth (replaces business-v2 middleware.ts cookie gate). An
    // unauthenticated visit to "/" → "/dashboard" → /sign-in?next=/dashboard.
    path: "/",
    element: <RequireAuth />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          // One gate for every shell route (NavRouteGate): a route the menu hides
          // from this person renders the no-access state; detail routes follow
          // their page. Unclaimed URLs keep their own guards.
          {
            element: <NavRouteGate />,
            children: [
              // Layer 1 — the index gate. Reads org status and routes the owner to
              // the single correct destination (getting-started / facility-settings /
              // dashboard) or renders the rejected screen inline. Replaces the old
              // unconditional `<Navigate to="/dashboard">`.
              { index: true, element: <OnboardingRouter /> },
              // Onboarding surfaces — reachable WHILE the org is locked (they're how
              // the owner finishes setup), so they sit OUTSIDE RequireApprovedOrg.
              { path: "getting-started", element: <GettingStarted /> },
              { path: "pending", element: <PendingConfirmationPage /> },
              // Notifications inbox — USER-SCOPED, not facility-scoped. It sits
              // OUTSIDE RequireApprovedOrg (a pending org must still read its
              // notifications, not be bounced to /getting-started) and OUTSIDE
              // FacilityGate (a no-facility operator must not get the "No facility
              // selected" prompt). Sibling of /getting-started, /pending, /org.
              { path: "notifications", element: <NotificationsPage /> },
              { path: "places/:placeId/claim", element: <ClaimPlacePage /> },
              {
                path: "tournaments/:id/review-order-of-play",
                element: <TournamentScheduleReviewPage />,
              },
              { path: "payments", element: <PaymentsWorkspace api={financeApi} /> },
              // Organization-owned offer/history declarations; no selected facility or
              // operational approval gate. Every server read still requires current Owner.
              { path: "commercial/offers", element: <ModuleOffersPage /> },
              {
                path: "commercial/offers/:versionId",
                element: <ModuleOfferReviewPage />,
              },
              { path: "commercial/agreements", element: <ModuleCommitmentsPage /> },
              {
                path: "commercial/agreements/:acceptanceId",
                element: <ModuleAgreementPage />,
              },
              {
                path: "commercial/readiness",
                element: <CommercialReadinessPage />,
              },
              // Coach SELF-portal — USER-SCOPED, not facility-scoped (distinct from
              // the operator-side /coaches surface that MANAGES a facility's
              // coaches). GET /coaching/income returns the caller's OWN income (no
              // id) and GET /lesson-requests?coach_user_id={me} lists the caller's
              // OWN inbox. A coach with a pending org and NO facility selected must
              // still reach it, so — exactly like /notifications — it sits OUTSIDE
              // RequireApprovedOrg + FacilityGate. A non-coach sees an honest empty
              // income + empty inbox.
              { path: "coaching", element: <CoachWorkspace /> },
              { path: "coaching/:requestId", element: <CoachingRequestDetail /> },
              { path: "inquiries", element: <InquiryWorkspace /> },
              { path: "inquiries/:conversationId", element: <InquiryThread /> },
              // Org-level surfaces that own their OWN status check and must be
              // reachable without a facility selection (they live outside the
              // facility gate via ORG_LEVEL_PREFIXES). /org self-redirects a
              // non-approved org to /getting-started; /facilities/new is the create
              // flow an operator with zero facilities uses to make their first one.
              { path: "org", element: <OrgDashboardPage /> },
              { path: "facilities/new", element: <CreateFacility /> },
              // Layer 2 + Layer 3 — operational routes are gated first on org
              // approval (RequireApprovedOrg), then on a selected facility
              // (FacilityGate). Org-level paths pass the facility gate via
              // isOrgLevelRoute; operational paths require a facility selection.
              // Private court inventory and team setup have explicit sibling
              // status gates below; this operational block stays approval-only.
              {
                element: <RequireApprovedOrg />,
                children: [
                  {
                    path: "tournaments/:id/control-center",
                    element: <TournamentControlCenterPage />,
                  },
                  {
                    element: <FacilityGate />,
                    children: [
                      { path: "dashboard", element: <OperatorDashboard /> },
                      { path: "arena", element: <ArenaWorkspace /> },
                      // prettier-ignore
                      { path: "queue", element: <Navigate to="/services/drop-in?tab=dashboard" replace /> },
                      // Front Desk arrivals console (F9) — facility-scoped (RBAC
                      // facility:checkin), so it gates exactly like /check-in and /arena:
                      // under DashboardLayout INSIDE RequireApprovedOrg + FacilityGate.
                      // SEPARATE surface from the QR-redeem /check-in tab.
                      { path: "front-desk", element: <ArrivalsConsole /> },
                      // Front desk › Devices: the scan kiosks (#2691), same gate.
                      { path: "front-desk/devices", element: <KioskDevicesPage /> },
                      { path: "check-in", element: <CheckInWorkspace /> },
                      { path: "calendar", element: <CalendarWorkspace /> },
                      { path: "bookings", element: <BookingsWorkspace /> },
                      { path: "tournaments", element: <TournamentsWorkspace /> },
                      { path: "tournaments/new", element: <NewTournamentPage /> },
                      { path: "tournaments/:id", element: <TournamentDetail /> },
                      // Orphan /registrations → canonical Entries tab (one authoritative
                      // surface — the entries grid lives inside the detail shell now).
                      {
                        path: "tournaments/:id/registrations",
                        element: <TournamentTabRedirect tab="entries" />,
                      },
                      // Legacy deep-link shims → detail page with the matching ?tab=.
                      {
                        path: "tournaments/:id/draw",
                        element: <TournamentTabRedirect tab="draws" />,
                      },
                      {
                        path: "tournaments/:id/live",
                        element: <TournamentTabRedirect tab="bracket" />,
                      },
                      {
                        path: "tournaments/:id/results",
                        element: <TournamentTabRedirect tab="winners" />,
                      },
                      // Programs — list / create / detail. `new` is a static segment matched
                      // before the `:programId` dynamic param by the data router.
                      { path: "programs", element: <ProgramsList /> },
                      { path: "programs/new", element: <ProgramCreate /> },
                      { path: "programs/:programId", element: <ProgramDetail /> },
                      { path: "settings/facility", element: <FacilitySettings /> },
                      { path: "settings/org", element: <OrgSettings /> },
                      // Platform contract review/accept — ORG-level (the org ↔ platform
                      // agreement, not facility-scoped): reachable with zero facilities via
                      // ORG_LEVEL_PREFIXES. Pending orgs are bounced to /getting-started by
                      // RequireApprovedOrg (they review the contract inside getting-started's
                      // ContractTermsPanel instead).
                      { path: "settings/contract", element: <ContractPage /> },
                      // Memberships — real plans/visit-packs surface. Promotions is a REAL,
                      // facility-scoped page (F4 Slice 3) — it replaced the ComingSoon stub.
                      // list + create + delete live there (no edit endpoint, send deferred).
                      { path: "memberships", element: <MembershipsPage /> },
                      {
                        path: "promotions",
                        element: <PromotionsWorkspace />,
                      },
                      // Moved from /memberships/promotions (79b66953c); keep old links working.
                      { path: "memberships/promotions", element: <Navigate to="/promotions" replace /> },

                      // Analytics workspaces — real, facility-scoped pages (inside
                      // RequireApprovedOrg + FacilityGate). Every number comes from a
                      // backend field; no fabricated data. These replaced the v2 ComingSoon
                      // stubs.
                      {
                        path: "analytics/revenue",
                        element: <RevenueAnalyticsWorkspace />,
                      },
                      {
                        path: "analytics/bookings",
                        element: <BookingAnalyticsWorkspace />,
                      },
                      {
                        path: "analytics/insights",
                        element: <OperationalInsightsWorkspace />,
                      },
                      {
                        path: "analytics/insights/programs",
                        element: <ProgramInsightsWorkspace />,
                      },
                      {
                        path: "analytics/insights/growth",
                        element: <GrowthInsightsWorkspace />,
                      },
                      {
                        path: "analytics/insights/instructors",
                        element: <InstructorInsightsWorkspace />,
                      },
                      {
                        path: "analytics/insights/retention",
                        element: <ServiceRetentionInsightsWorkspace />,
                      },
                      // Earnings (settlement) workspace — a facility OWNER's payout view
                      // (gross → platform-fee deduction → refunds → net owed, by period).
                      // Same facility-scoped gating as the sibling analytics pages.
                      {
                        path: "analytics/earnings",
                        element: <SettlementsWorkspace />,
                      },
                      // `/classes` duplicated the real `/programs` capability — class
                      // programs ARE programs. Redirect rather than keep a dead ComingSoon
                      // stub so legacy links + the Classes nav entry land on the real page.
                      {
                        path: "classes",
                        element: <Navigate to="/programs" replace />,
                      },
                      // Leagues — real, facility-scoped page (F6 Slice 1). Replaced the
                      // ComingSoon stub. List + create live here; the detail shell prevents
                      // a dead link until the season/division tabs land in Slice 2.
                      // Community — the facility's single "club" (S2.2). One page, two states
                      // (create form / manage view) for the selected facility. Facility-scoped,
                      // so it gates exactly like /leagues: inside RequireApprovedOrg +
                      // FacilityGate. RBAC (facility:community / community:manage) is enforced
                      // server-side; the page only shows/enables the controls accordingly.
                      { path: "community", element: <CommunityPage /> },
                      { path: "leagues", element: <LeaguesList /> },
                      // Static `leagues/seasons/:seasonId` is declared before the dynamic
                      // `leagues/:leagueId` for readability; the data router matches the
                      // static `seasons` segment first regardless of declaration order, so
                      // the season-detail route never collides with a league_id.
                      {
                        path: "leagues/seasons/:seasonId",
                        element: <SeasonDetail />,
                      },
                      { path: "leagues/:leagueId", element: <LeagueDetail /> },
                      { path: "settings/rates", element: <RatesWorkspace /> },
                      // Drop-in service home (#143 slice 3) — Setup + live Dashboard tabs,
                      // the canonical operational drop-in surface owners use.
                      { path: "services/drop-in", element: <DropInServiceHome /> },
                      // Court Reservation setup (#143 slice 4) — selected-facility court
                      // pricing and booking-policy configuration. Operational reservation
                      // search and actions remain exclusively at /bookings.
                      {
                        path: "services/court-reservation",
                        element: <CourtReservationServiceHome />,
                      },
                      {
                        path: "services/classes-coaching",
                        element: <Navigate to="/programs" replace />,
                      },
                      {
                        path: "services/memberships",
                        element: <Navigate to="/memberships" replace />,
                      },
                      // Legacy /settings/drop-in — its check-in-mode + staff config moved into
                      // the service home's Setup tab; kept only as a redirect there (clean-
                      // migration: one editing surface, not two). See drop-in-workspace.tsx.
                      { path: "settings/drop-in", element: <DropInWorkspace /> },
                    ],
                  },
                ],
              },
              // Court inventory is private preparation. Facility selection and the
              // server's facility:court permission still govern the existing editor.
              {
                element: (
                  <RequireApprovedOrg
                    allowedStatuses={COURT_SETUP_ALLOWED_STATUSES}
                  />
                ),
                children: [
                  {
                    element: <FacilityGate />,
                    children: [{ path: "courts", element: <CourtsWorkspace /> }],
                  },
                ],
              },
              // Coaches + Staff — another exception to Layer 2's default
              // (approved/active-only) rule: an owner must be able to set up their
              // team (add coaches, invite staff, define roles) WHILE the org is
              // still pending/in_review, not just after platform approval ("add
              // when needed"). So this sibling block uses RequireApprovedOrg with
              // the WIDER STAFF_SETUP_ALLOWED_STATUSES (pending/in_review/approved/
              // active) instead of the default — rejected/suspended stay locked
              // (bounced to /getting-started) exactly like every other operational
              // route, so this does NOT hand a restricted/terminal org a new
              // capability it didn't have. People is organization-scoped and does
              // not require selecting a facility; facility-dependent settings keep
              // their FacilityGate. The invited staff/coaches still can't
              // operate anywhere else (arena, queue, bookings, …) until the org
              // goes live — only THIS setup surface is reachable early. RBAC
              // (facility:manage / facility:coach, enforced server-side) is
              // untouched; this only relaxes the org-approval gate.
              {
                element: (
                  <RequireApprovedOrg
                    allowedStatuses={STAFF_SETUP_ALLOWED_STATUSES}
                  />
                ),
                children: [
                  // People is org-scoped; facility-dependent tabs keep their own scope.
                  { path: "staff", element: <StaffPage /> },
                  {
                    element: <FacilityGate />,
                    children: [
                      // Coaches — list / create / detail. coachUserId is a Firebase UID.
                      // Staff — roles, facility staff, and staff login accounts.
                      // This route also satisfies the coach-create "Go to Staff" button.
                      {
                        path: "settings/access",
                        element: <ScopedCapabilitiesPage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // Launch sequence — the un-folded Stripe Connect setup page. Going live
        // itself is a button ON the Getting Started workflow (a confirm dialog,
        // not a separate page — see getting-started.tsx). ORG-LEVEL sibling of
        // the DashboardLayout block: still under RequireAuth, but OUTSIDE both
        // DashboardLayout (no operational shell/sidebar — a locked org can't use
        // it) and RequireApprovedOrg (a pending org reaches it to finish Stripe
        // Connect). Self-guards on its own org read (no-org → /getting-started).
        element: <LaunchLayout />,
        children: [
          { path: "setup/stripe", element: <StripeConnectSetupPage /> },
          { path: "workspaces", element: <WorkspaceChooser /> },
          {
            path: "staff/invitations/:invitationId",
            element: <StaffInvitationPage />,
          },
        ],
      },
    ],
  },
  {
    // Public legal pages — ported verbatim from business-v2 /privacy and /terms.
    // Mounted OUTSIDE both the dashboard shell and RequireAuth: these must be
    // reachable by an unauthenticated visitor (e.g. the sign-up footer links to
    // them). Each page is a self-contained full-screen document with its own
    // sticky header, so it needs no wrapping layout.
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
  },
  {
    // Catch-all — render the ported 404 inside the auth chrome.
    path: "*",
    element: <AuthLayout />,
    children: [{ path: "*", element: <NotFoundPage /> }],
  },
]);

// The data-layer client is owned HERE (not in app/providers) so the router is a
// self-contained unit: <Router/> renders with only an AuthContext above it,
// which is exactly how the route-placement tests exercise the real
// createBrowserRouter config (a QueryClientProvider wrapper would otherwise be a
// hidden test-only dependency). App composes <Router/> under theme + auth; this
// is the single QueryClientProvider in the tree.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

export function Router() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Wipes the React Query cache when the authed identity changes, so a same-
          tab sign-out → sign-in can never let the next operator read the previous
          one's cached, user-scoped data (notifications/profile PII). Mounted
          INSIDE the client (needs useQueryClient) and below the AuthProvider that
          App supplies (needs useAuth). */}
      <FinanceConnectIdentityBoundary>
        <QueryCacheAuthBoundary>
          <OrganizationProvider>
            <RouterProvider router={router} />
          </OrganizationProvider>
        </QueryCacheAuthBoundary>
      </FinanceConnectIdentityBoundary>
    </QueryClientProvider>
  );
}
